import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UrlPathGameZone } from "../../../url";
import API from "../../../api/Api";

interface Mission {
  title: string;
  desc: string;
  progress: number;
  reward: string;
  status: string;
}

const MissionCenter: React.FC = () => {
  const navigate = useNavigate();
  const [paymentCount, setPaymentCount] = useState(0);
  const [emailCorrespondenceAllowed, setEmailCorrespondenceAllowed] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
  
  const checkPaymentHistory = useCallback(async () => {
    try {
      const history = await API.getCustomerHistory();
      const historyArray = Array.isArray(history) ? history : [];
      const payments = historyArray.filter((item: any) => 
        item.interaction?.interactionType === 'PAYMENT_INTERACTION'
      );
      setPaymentCount(payments.length);
    } catch (error) {
      console.error('Error fetching customer history:', error);
    }
  }, []);

  const checkCustomerDetails = useCallback(async () => {
    try {
      const customer = await API.findCustomer();
      setEmailCorrespondenceAllowed(customer?.emailCorrespondenceAllowed || false);
      setDateOfBirth(customer?.dateOfBirth || null);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  }, []);

  useEffect(() => {
    checkPaymentHistory();
    checkCustomerDetails();
  }, [checkPaymentHistory, checkCustomerDetails]);
  
  const handleRedeemReward = (mission: Mission) => {
    if (mission.title.includes('Profil')) {
      navigate(UrlPathGameZone);
    } else if (mission.title.includes('Newsletter')) {
      navigate(UrlPathGameZone + '?tab=memory');
    } else if (mission.title.includes('Einkäufe')) {
      navigate(UrlPathGameZone + '?tab=scratch');
    }
  };

  const getShoppingMission = () => {
    const completed = paymentCount >= 3;
    return {
      title: '🛍️ 3 Einkäufe tätigen',
      desc: 'Zeige deine Treue und kaufe 3× ein – sammle dabei wertvolle Prämien.',
      progress: Math.min((paymentCount / 3) * 100, 100),
      reward: '🎁 Rubbellos aktiv',
      status: completed ? 'Erledigt' : `${paymentCount}/3 Einkäufe`
    };
  };

  const getNewsletterMission = () => {
    return {
      title: '💌 Newsletter abonnieren',
      desc: 'Erhalte exklusive Angebote und Spiel-Benachrichtigungen.',
      progress: emailCorrespondenceAllowed ? 100 : 0,
      reward: '🧠 Memory-Spiel freischalten',
      status: emailCorrespondenceAllowed ? 'Erledigt' : 'Offen'
    };
  };

  const getProfileMission = () => {
    const hasDateOfBirth = !!dateOfBirth;
    return {
      title: '🎯 Profil vervollständigen',
      desc: 'Füge dein Geburtsdatum, Interessen und Lieblingsfiliale hinzu.',
      progress: hasDateOfBirth ? 100 : 0,
      reward: '🎡 Glücksrad freischalten',
      status: hasDateOfBirth ? 'Erledigt' : 'Offen'
    };
  };

  const missions: Mission[] = [
    getProfileMission(),
    getShoppingMission(),
    getNewsletterMission(),
    { title: '⭐ Produkt bewerten', desc: 'Teile deine Meinung mit anderen Mitgliedern.', progress: 0, reward: '💎 +50 Punkte', status: 'Offen' },
    { title: '👫 2 Freunde einladen', desc: 'Lade zwei Freunde ein und drehe nochmals am Glücksrad!', progress: 0, reward: '🎡 Glücksrad Bonus-Dreh', status: '0/2 Freunde' },
  ];

  const overallProgress = (missions.reduce((sum, m) => sum + m.progress, 0) / missions.length).toFixed(0);

  return (
    <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <h1>🧭 Mission Center</h1>
        <p>Sammle Punkte, schalte Spiele frei und entdecke neue Überraschungen! 🚀</p>
      </div>

      <div style={{
        background: 'linear-gradient(to right, #ccf1f5, #f1f1f4, #c7bbb6)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ fontWeight: '600', fontSize: '18px', margin: '0 0 8px 0' }}>
          🎉 Dein Fortschritt: <span style={{ color: 'rgb(121, 111, 103)' }}>{overallProgress}%</span>
        </p>
        <div style={{
          width: '100%',
          backgroundColor: '#e5e7eb',
          borderRadius: '9999px',
          height: '8px',
          overflow: 'hidden',
          margin: '8px 0'
        }}>
          <div style={{
            height: '8px',
            borderRadius: '9999px',
            width: `${overallProgress}%`,
            background: 'linear-gradient(90deg, #191919, #796f67, #c7bbb6)'
          }} />
        </div>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>Noch Missionen freischalten! Du schaffst das! 🪄</p>
      </div>

      <section>
        <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>🔥 Aktive Missionen</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {missions.map((mission) => (
            <div key={mission.title}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '2px solid transparent',
                transition: 'all 0.3s ease'
              }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {mission.title}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px 0' }}>{mission.desc}</p>
              <div style={{
                width: '100%',
                backgroundColor: '#e5e7eb',
                borderRadius: '9999px',
                height: '8px',
                overflow: 'hidden',
                margin: '8px 0'
              }}>
                <div style={{
                  height: '8px',
                  borderRadius: '9999px',
                  width: `${mission.progress}%`,
                  background: 'linear-gradient(90deg, #06b6d4, #7c3aed)'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', margin: '8px 0 12px 0' }}>
                <span style={{ fontWeight: '500' }}>{mission.reward}</span>
                <span style={{ color: '#6b7280' }}>{mission.status}</span>
              </div>
              <button 
                onClick={mission.status === 'Erledigt' ? () => handleRedeemReward(mission) : undefined}
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: mission.status === 'Erledigt' ? '#10b981' : '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {mission.status === 'Erledigt' ? '🎮 Belohnung einlösen' : '🎮 Jetzt starten'}
              </button>
            </div>
          ))}
        </div>
      </section>

      <aside style={{ marginTop: '48px', textAlign: 'center' }}>
        <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px' }}>🏅 Erfolg & Status-Badges</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px' }}>
          {[
            { icon: '🥇', title: 'Treueheld', desc: '10 Missionen', reward: 'Exklusiver Status' },
            { icon: '🧩', title: 'Spielemeister', desc: 'Alle Spiele', reward: 'Bonus Punkte' },
            { icon: '💌', title: 'Markenbotschafter', desc: '3 Freunde', reward: 'Spezial Coupon' },
            { icon: '🔥', title: 'Power Shopper', desc: '5+ Einkäufe', reward: 'VIP Zugang' }
          ].map((badge) => (
            <div key={badge.title}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                background: 'linear-gradient(to bottom, #fefce8, #ffffff)',
                border: '1px solid #fde68a',
                borderRadius: '50%',
                width: '160px',
                height: '160px',
                boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
                padding: '16px'
              }}>
              <span style={{ fontSize: '48px', marginBottom: '8px' }}>{badge.icon}</span>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'rgb(121, 111, 103)', margin: '0 0 4px 0' }}>{badge.title}</h4>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px 0' }}>{badge.desc}</p>
              <p style={{ fontSize: '12px', fontWeight: '500', margin: '0', color: '#b45309' }}>{badge.reward}</p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};

export default MissionCenter;